import React, { useEffect, useMemo, useState } from 'react';
import { Shield, Mail, UserCircle, Lock, CheckCircle2, AlertTriangle } from 'lucide-react';
import { lookupInvite, registerWithInvite } from '../services/apiClient';

function getTokenFromLocation() {
  const pathParts = window.location.pathname.split('/').filter(Boolean);
  const inviteIndex = pathParts.indexOf('invite');
  if (inviteIndex >= 0 && pathParts[inviteIndex + 1]) return decodeURIComponent(pathParts[inviteIndex + 1]);
  const params = new URLSearchParams(window.location.search);
  return params.get('token') || '';
}

export default function InviteRegisterPage({ onRegistered, onBackToLogin }) {
  const token = useMemo(() => getTokenFromLocation(), []);
  const [loading, setLoading] = useState(true);
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState('');
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState('');

  useEffect(() => {
    let alive = true;
    async function load() {
      setLoading(true);
      const result = token ? await lookupInvite(token) : { ok: false, message: 'Missing invitation token.' };
      if (!alive) return;
      if (result?.ok) {
        setInvite(result.invite);
        setName(result.invite?.name || '');
        setError('');
      } else {
        setError(result?.message || 'This invitation link is not valid.');
      }
      setLoading(false);
    }
    load();
    return () => { alive = false; };
  }, [token]);

  async function handleSubmit(event) {
    event.preventDefault();
    setError('');
    setSuccess('');
    if (!name.trim()) {
      setError('Please enter your name.');
      return;
    }
    if (password.length < 10) {
      setError('Use a password or passphrase with at least 10 characters.');
      return;
    }
    if (password !== confirmPassword) {
      setError('The passwords do not match.');
      return;
    }
    setSubmitting(true);
    const result = await registerWithInvite({ token, name, password });
    setSubmitting(false);
    if (result?.ok && result.user) {
      setSuccess('Account created. Signing you in now...');
      onRegistered?.(result.user);
      return;
    }
    setError(result?.message || 'Registration failed. Please ask the admin for a new invite.');
  }

  return (
    <div className="min-h-screen bg-mentor-bg flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-xl">
        <div className="text-center mb-6">
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center shadow-lg shadow-blue-500/20 mx-auto mb-4">
            <Shield size={30} className="text-white" />
          </div>
          <h1 className="font-display text-3xl font-bold text-mentor-text">Bond Mentor Invitation</h1>
          <p className="text-sm text-mentor-muted mt-2">Create your account from this secure invite link.</p>
        </div>

        <div className="glass-card border border-mentor-border/70 p-6">
          {loading && (
            <div className="text-center py-8">
              <div className="w-10 h-10 rounded-full border-2 border-mentor-accent/30 border-t-mentor-accent animate-spin mx-auto mb-4" />
              <p className="font-display font-bold text-mentor-text">Checking invitation...</p>
            </div>
          )}

          {!loading && error && !invite && (
            <div className="rounded-2xl border border-signal-red/30 bg-signal-red/10 p-5 text-center">
              <AlertTriangle size={28} className="text-signal-red mx-auto mb-3" />
              <h2 className="font-display font-bold text-lg text-mentor-text">Invite Not Available</h2>
              <p className="text-sm text-mentor-muted mt-2">{error}</p>
              <button type="button" onClick={onBackToLogin} className="mt-5 rounded-xl border border-mentor-border/60 px-4 py-2 text-sm font-display font-semibold text-mentor-text hover:border-mentor-accent/60 transition-colors">
                Back to Sign In
              </button>
            </div>
          )}

          {!loading && invite && (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="rounded-2xl border border-mentor-accent/30 bg-mentor-accent/10 p-4">
                <div className="flex items-start gap-3">
                  <CheckCircle2 size={20} className="text-mentor-accent mt-0.5" />
                  <div>
                    <p className="font-display font-bold text-mentor-text">Valid invitation found</p>
                    <p className="text-sm text-mentor-muted mt-1">This invite is tied to <span className="font-mono text-mentor-text">{invite.email}</span>.</p>
                    <p className="text-xs text-mentor-muted mt-2">Role: {invite.role} · Tier: {invite.tier} · Expires: {new Date(invite.expiresAt).toLocaleString()}</p>
                  </div>
                </div>
              </div>

              {error && <div className="rounded-xl border border-signal-red/30 bg-signal-red/10 px-4 py-3 text-sm text-red-100">{error}</div>}
              {success && <div className="rounded-xl border border-signal-green/30 bg-signal-green/10 px-4 py-3 text-sm text-green-100">{success}</div>}

              <label className="block">
                <span className="text-xs font-display font-bold uppercase tracking-widest text-mentor-muted">Email</span>
                <div className="mt-1 flex items-center gap-2 rounded-xl border border-mentor-border/60 bg-mentor-surface/80 px-3 py-3">
                  <Mail size={16} className="text-mentor-muted" />
                  <input value={invite.email} disabled className="w-full bg-transparent text-mentor-muted font-mono text-sm outline-none" />
                </div>
              </label>

              <label className="block">
                <span className="text-xs font-display font-bold uppercase tracking-widest text-mentor-muted">Your Name</span>
                <div className="mt-1 flex items-center gap-2 rounded-xl border border-mentor-border/60 bg-mentor-surface/80 px-3 py-3 focus-within:border-mentor-accent/50">
                  <UserCircle size={16} className="text-mentor-muted" />
                  <input value={name} onChange={(e) => setName(e.target.value)} className="w-full bg-transparent text-mentor-text text-sm outline-none" placeholder="Your full name" />
                </div>
              </label>

              <label className="block">
                <span className="text-xs font-display font-bold uppercase tracking-widest text-mentor-muted">Create Password</span>
                <div className="mt-1 flex items-center gap-2 rounded-xl border border-mentor-border/60 bg-mentor-surface/80 px-3 py-3 focus-within:border-mentor-accent/50">
                  <Lock size={16} className="text-mentor-muted" />
                  <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="w-full bg-transparent text-mentor-text text-sm outline-none" placeholder="At least 10 characters" />
                </div>
              </label>

              <label className="block">
                <span className="text-xs font-display font-bold uppercase tracking-widest text-mentor-muted">Confirm Password</span>
                <div className="mt-1 flex items-center gap-2 rounded-xl border border-mentor-border/60 bg-mentor-surface/80 px-3 py-3 focus-within:border-mentor-accent/50">
                  <Lock size={16} className="text-mentor-muted" />
                  <input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} className="w-full bg-transparent text-mentor-text text-sm outline-none" placeholder="Type the same password again" />
                </div>
              </label>

              <button disabled={submitting} className="w-full rounded-xl bg-mentor-accent hover:bg-mentor-accent/90 disabled:opacity-60 px-4 py-3 font-display font-bold text-white transition-colors">
                {submitting ? 'Creating Account...' : 'Create Account & Sign In'}
              </button>

              <p className="text-xs text-mentor-muted leading-relaxed text-center">
                This invite is single-use. After the account is created, the link cannot be reused.
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
